import { Link } from "react-router-dom";

export interface RelatedPillarLink {
  /** Route path including leading slash, e.g. "/ms-fatigue-tracker". */
  to: string;
  /** Keyword-rich anchor text. Kept lowercase by convention for in-content links. */
  label: string;
}

export interface RelatedPillarsProps {
  /** Lead-in phrase before the links. Defaults to "Keep reading". */
  intro?: "Keep reading" | "Related" | "Go deeper" | string;
  /** 2–3 sibling pillars to link to. */
  links: RelatedPillarLink[];
  /** Optional trailing phrase after the last link, before the period. */
  trail?: string;
  className?: string;
}

/**
 * In-content "Related pillars" paragraph used at the bottom of every pillar page,
 * just above the closing CTA. Keeps anchor text and styling consistent so the
 * internal-linking checker keeps passing.
 *
 * Renders as:
 *   {intro}: <Link>label A</Link>, <Link>label B</Link>{trail ? " " + trail : ""}.
 */
const joinLinks = (links: RelatedPillarLink[]) => {
  return links.map((link, i) => {
    const isLast = i === links.length - 1;
    const isSecondToLast = i === links.length - 2;
    const sep = isLast ? "" : isSecondToLast && links.length === 2 ? " and " : isSecondToLast ? ", and " : ", ";
    return (
      <span key={link.to}>
        <Link
          to={link.to}
          className="text-primary underline underline-offset-4 hover:opacity-80 transition-opacity"
        >
          {link.label}
        </Link>
        {sep}
      </span>
    );
  });
};

export const RelatedPillars = ({
  intro = "Keep reading",
  links,
  trail,
  className,
}: RelatedPillarsProps) => {
  if (links.length === 0) return null;

  return (
    <section
      className={
        className ??
        "container mx-auto px-6 sm:px-10 lg:px-16 py-12"
      }
      aria-label="Related pillar pages"
    >
      <p className="text-center text-muted-foreground max-w-2xl mx-auto">
        {intro}: {joinLinks(links)}
        {trail ? ` ${trail}` : ""}.
      </p>
    </section>
  );
};

export default RelatedPillars;
